import { gql } from '@apollo/client';
import { captureException } from '@sentry/nextjs';
import { concat, flatten } from 'lodash';
import * as Sentry from '@sentry/react';
import { convertZeroToOneIndexIfNumber, generateUUID } from '../Utils/utils';
import { getCampaignIdTrumpedByUrl } from './campaignId';
import {
  componentContainsCheckboxOptions,
  getValueFromComponent,
  isFormInputComponent,
} from './components';
import { exposeEventToPartner } from './events';
import { getEncryptedAnswers } from './getEncryptedAnswers';
import { isConditionallyActive } from './conditionality';
import {
  getEmailComponent,
  hasPhoneAdmiralId,
} from '../Components/__helpers__/component.helpers';
import Subcategories from '../Data/Subcategories';
import { SELECT_COUNTRY_MAPPED_QUESTION_ID } from '../Data/Country';
import { matchLevels } from '../Lib/redux/sagas/getCustomerMatches/matchLevels';
import CustomerJourneyType from '../Data/CustomerJourneyType';
import { shouldShowSingleSupplierStatement } from '../Components/LegalStatement/legalStatement.helpers';
import { getSupplierMatchComponent } from './supplierMatchPredicates';
import { isAnswerUsableForAiMatching } from './getNonPiiQuestionTexts';
import {
  getMatchedStrategyCollection,
  getOutreachAgentMethod,
} from './strategyCollection.helpers';
import { OUTREACH_AGENT_METHOD_MAPPED_QUESTION_ID } from '../Data/OutreachAgentMethod';

export const SUBMIT_LEAD_GQL = gql`
  mutation SubmitLead($submission: SubmissionInput!, $visitorId: String!) {
    submitLead(submission: $submission, visitorId: $visitorId) {
      id
      submissionToken
    }
  }
`;

export const SUBMIT_LEAD_WITH_APPOINTMENT_BOOKING_GQL = gql`
  mutation SubmitLead(
    $submission: SubmissionInput!
    $appointmentBooking: AppointmentBookingInput
    $visitorId: String
  ) {
    submitLead(
      submission: $submission
      appointmentBooking: $appointmentBooking
      visitorId: $visitorId
    ) {
      id
      submissionToken
      appointmentId
    }
  }
`;

/**
 *  consentedSuppliersHash and consentedLegalStatementHash are here as props as they do not exist in state and can not be selected from the redux store outside of a hook or react component - and submitLead is a function, which is conditionally called (thus can't be turned into a hook).
 */

export const getUrl = (pageUrl) => {
  if (pageUrl) {
    return pageUrl;
  }

  return window.location !== window.parent.location
    ? document.referrer
    : document.location.href;
};

export const exposeFormSubmitEvent = ({
  submissionArgs,
  components,
  eventPublicKeyString,
  submissionResponse,
  iFrameId,
  isDomainInternal,
  submissionPage,
  currentPage,
  appointmentId,
  formId,
  formOriginId,
  submissionType,
  dynamicBrandMatch,
}) => {
  if (eventPublicKeyString && isDomainInternal) {
    /**
     * We want to expose the event to the partner, but we also don't want to expose PII data, i.e. so we should only expose the pii data in the event if the domain is whitelisted (i.e. an internal domain)
     *
     * if eventPublicKeyString is present then the mvfTranslationLayer is ready to receive encryptedAnswers
     */
    getEncryptedAnswers(
      submissionArgs.lead.formComponents,
      components,
      eventPublicKeyString,
      formId
    )
      .then((encryptedAnswers) => {
        // Add a custom tag to the tracked Chameleon session in Sentry - relevant if a runtime error occurred in this session (to help produce a submission percentage for an issue alert rule via session filtering)
        Sentry.setTag('chameleon_form_submitted', true);
        exposeEventToPartner({
          eventType: 'formSubmit',
          eventObject: {
            submissionId: submissionResponse.id,
            iFrameId,
            encryptedAnswers,
            submissionPage: convertZeroToOneIndexIfNumber(submissionPage),
            fromPage: convertZeroToOneIndexIfNumber(currentPage),
            appointmentId,
            formId,
            formOriginId,
            ...(submissionType !== undefined && { submissionType }),
            ...(dynamicBrandMatch !== undefined && { dynamicBrandMatch }),
          },
        });
      })
      .catch((error) => {
        captureException(error);
      });
  } else {
    /**
     * There are no encryptedAnswers, i.e. no PII so we can send the event
     */
    // Add a custom tag to the tracked Chameleon session in Sentry - relevant if a runtime error occurred in this session (to help produce a submission percentage for an issue alert rule via session filtering)
    Sentry.setTag('chameleon_form_submitted', true);
    exposeEventToPartner({
      eventType: 'formSubmit',
      eventObject: {
        submissionId: submissionResponse.id,
        iFrameId,
        submissionPage: convertZeroToOneIndexIfNumber(submissionPage),
        fromPage: convertZeroToOneIndexIfNumber(currentPage),
        appointmentId,
        formId,
        formOriginId,
        ...(submissionType !== undefined && { submissionType }),
        ...(dynamicBrandMatch !== undefined && { dynamicBrandMatch }),
      },
    });
  }
};

export const exposePartialFormSubmitEvent = ({
  iFrameId,
  partialSubmissionPage,
  submissionId,
  components,
  isDomainInternal,
}) => {
  let email;

  if (isDomainInternal) {
    const emailComponent = getEmailComponent({ components });
    email = emailComponent?.input?.value;
  } else {
    email = '********';
  }

  exposeEventToPartner({
    eventType: 'partialFormSubmit',
    eventObject: {
      iFrameId,
      partialSubmissionPage: convertZeroToOneIndexIfNumber(
        partialSubmissionPage
      ),
      submissionId,
      email,
    },
  });
};

/**
 * Secondary "Get Quotes" event for phone-step-conversion journeys, where the
 * primary formSubmit already fired at the phone step. Carries the canonical
 * submissionId only — no PII — so the results-page step is independently
 * measurable without double-counting the primary. fromPage/submissionPage let
 * the translation layer attribute the supplier match events to this click
 * rather than to the earlier phone-step formSubmit.
 */
export const exposeResultsPageQuotesRequestedEvent = ({
  iFrameId,
  submissionResponse,
  submissionType,
  selectedSuppliers,
  submissionPage,
  currentPage,
}) => {
  exposeEventToPartner({
    eventType: 'resultsPageSubmit',
    eventObject: {
      submissionId: submissionResponse?.id,
      iFrameId,
      submissionPage: convertZeroToOneIndexIfNumber(submissionPage),
      fromPage: convertZeroToOneIndexIfNumber(currentPage),
      ...(submissionType !== undefined && { submissionType }),
      ...(selectedSuppliers !== undefined && { selectedSuppliers }),
    },
  });
};

const isConditionallyHiddenSupplierMatch = (pageComponent, pageComponents) => {
  return (
    pageComponent.component.type === 'supplier-match' &&
    !isConditionallyActive(pageComponents, pageComponent)
  );
};

/**
 * Capture reports this field as the post-submission method, so it is submitted
 * empty unless a matched subscription has a collection on the form for that
 * method.
 *
 * Every method reports the same way. Requiring that Chameleon could run the
 * collection would blank ELEVEN_LABS on AI-calling forms, whose collection
 * carries no steps for us.
 */
const shouldSuppressOutreachAgentMethod = ({
  pageComponent,
  hasMatchedStrategyCollection,
}) => {
  if (
    pageComponent.component.mappedQuestionId !==
    OUTREACH_AGENT_METHOD_MAPPED_QUESTION_ID
  ) {
    return false;
  }

  return !hasMatchedStrategyCollection;
};

export const convertComponentToSubmissionObject = (
  pageComponent,
  pageComponents,
  hasMatchedStrategyCollection = false
) => {
  const submission = {
    id: pageComponent.component.id.toString(),
  };

  const value = getValueFromComponent(pageComponent);

  if (componentContainsCheckboxOptions(pageComponent)) {
    // Currently we do not filter out conditionally hidden components but consider supplier match components as unselected if they are conditionally hidden. We may look to extending this to all conditionally hidden components in the future.
    return {
      ...submission,
      value:
        value &&
        value.length > 0 &&
        !isConditionallyHiddenSupplierMatch(pageComponent, pageComponents)
          ? value
          : [''],
      type: 'multiple',
    };
  }

  return {
    ...submission,
    value: [
      shouldSuppressOutreachAgentMethod({
        pageComponent,
        hasMatchedStrategyCollection,
      })
        ? ''
        : value || '',
    ],
    type: 'single',
  };
};

const getUrlForSubmission = (url, useCidFromURL = true) => {
  if (useCidFromURL === false) {
    const urlObj = new URL(url);
    urlObj.searchParams.delete('cid');
    return urlObj.toString();
  }

  return url;
};

const convertMatchesIntoBrands = ({ brandsOptionIds, matches }) => {
  const selectedBrands = brandsOptionIds.flatMap((optionId) => {
    return matches.find((potentialMatch) => {
      return potentialMatch.brandId === optionId;
    });
  });

  return selectedBrands.map((brandMatch) => {
    const brand = {
      brandId: brandMatch.brandId,
      brandRevisionId: brandMatch.brandRevisionId,
    };

    if (Number.isFinite(brandMatch.matchScore)) {
      brand.matchScore = brandMatch.matchScore;
    }

    return brand;
  });
};

const getBrandsShownToCustomer = (
  formInputComponents,
  customerBrandMatches,
  subcategoryBrandMatches,
  supplierMatchSelectionEnabled,
  isPredefinedSupplierWithBrandAvailable,
  dynamicSelectAndConnect,
  dynamicMatches
) => {
  if (dynamicSelectAndConnect?.enableDynamicSelectAndConnect) {
    return (dynamicMatches ?? []).map((match) => ({
      brandId: match.brandId,
      brandRevisionId: match.brandRevisionId,
    }));
  }

  if (isPredefinedSupplierWithBrandAvailable) {
    const brandMatch = customerBrandMatches[0];
    return [
      {
        brandId: brandMatch.brandId,
        brandRevisionId: brandMatch.brandRevisionId,
      },
    ];
  }
  const supplierMatchComponents = formInputComponents.filter(
    (component) =>
      component.component.type === 'supplier-match' &&
      !isConditionallyHiddenSupplierMatch(component, formInputComponents)
  );

  if (!supplierMatchSelectionEnabled || supplierMatchComponents.length === 0) {
    return undefined;
  }

  const shownBrandsOptionIds = flatten(
    supplierMatchComponents.map((component) => {
      return component.input.options.map((option) => {
        return option.id;
      });
    })
  );

  if (customerBrandMatches.some((match) => match.brandId)) {
    // We have customer matches with brand IDs, so send the new fields in the submission
    return convertMatchesIntoBrands({
      brandsOptionIds: shownBrandsOptionIds,
      matches: customerBrandMatches,
    });
  }

  if (subcategoryBrandMatches.some((match) => match.brandId)) {
    // We have failed to obtain customer matches but have access to the highest revenue fallback brands from capture so can use these.
    return convertMatchesIntoBrands({
      brandsOptionIds: shownBrandsOptionIds,
      matches: subcategoryBrandMatches,
    });
  }

  // Temporary workaround: If we do not have brand matches in the form of customer matches or the highest revenue fallback then we will need to utilise the admiral questions. This could be possible when opening up a new subcategory with no live suppliers. In the future we will want to handle this situation in another way such as simply failing submission and displaying some helpful message to the customer.

  const brandMatches = [];

  const getMatchingBrand = (matches) => (optionId) => {
    return matches.find((potentialMatch) => {
      return potentialMatch.optionId === optionId;
    });
  };

  // Check customer matches first to see if the user has selected any brands from there
  if (customerBrandMatches.length > 0) {
    brandMatches.push(
      ...shownBrandsOptionIds.flatMap(getMatchingBrand(customerBrandMatches))
    );
  }

  // If there are no customer matches, check the subcategory fallback matches
  if (customerBrandMatches.length === 0 && subcategoryBrandMatches.length > 0) {
    brandMatches.push(
      ...shownBrandsOptionIds.flatMap(getMatchingBrand(subcategoryBrandMatches))
    );
  }

  return brandMatches.map((brandMatch) => {
    return {
      brandFilterValue: brandMatch.brandFilterValue,
    };
  });
};

export const getBrandsConsentedByCustomer = (
  components,
  customerBrandMatches,
  subcategoryBrandMatches,
  supplierMatchSelectionEnabled,
  isPredefinedSupplierWithBrandAvailable,
  dynamicSelectAndConnect
) => {
  // Checked before the dynamic branch: when a single-supplier statement was
  // shown, the bundled hash is withheld from the submission, so the explicit
  // brand below is the only consent the lead can carry.
  if (isPredefinedSupplierWithBrandAvailable) {
    const brandMatch = customerBrandMatches[0];
    return [
      {
        brandId: brandMatch.brandId,
        brandRevisionId: brandMatch.brandRevisionId,
      },
    ];
  }

  // The dynamic Select and Connect primary consents to the full subcategory
  // list via consentedSuppliersHash, so it must send no explicit consent list:
  // the leads platform rebuilds the hash from brandsConsentedByCustomer
  // whenever it is non-empty, overriding the subcategory-wide opt-in
  // (GOLD-2441). Secondaries consent to themselves on the post-submission
  // page via selectAndConnectBrand, which overrides this return value.
  if (dynamicSelectAndConnect?.enableDynamicSelectAndConnect) {
    return undefined;
  }

  const supplierMatchComponents = components.filter(
    (component) =>
      component.component.type === 'supplier-match' &&
      !isConditionallyHiddenSupplierMatch(component, components)
  );

  if (!supplierMatchSelectionEnabled || supplierMatchComponents.length === 0) {
    return undefined;
  }

  const selectedBrandsOptionIds = flatten(
    supplierMatchComponents.map((component) => {
      return component.input.selectedOptions;
    })
  );

  if (customerBrandMatches.some((match) => match.brandId)) {
    // We have customer matches with brand IDs, so send the new fields in the submission
    return convertMatchesIntoBrands({
      brandsOptionIds: selectedBrandsOptionIds,
      matches: customerBrandMatches,
    });
  }

  if (subcategoryBrandMatches.some((match) => match.brandId)) {
    // We have failed to obtain customer matches but have access to the highest revenue fallback brands from capture so can use these.
    return convertMatchesIntoBrands({
      brandsOptionIds: selectedBrandsOptionIds,
      matches: subcategoryBrandMatches,
    });
  }

  // Temporary workaround: If we do not have brand matches in the form of customer matches or the highest revenue fallback then we will need to utilise the admiral questions. This could be possible when opening up a new subcategory with no live suppliers. In the future we will want to handle this situation in another way such as simply failing submission and displaying some helpful message to the customer.

  const brandMatches = [];

  const getMatchingBrand = (matches) => (optionId) => {
    return matches.find((potentialMatch) => {
      return potentialMatch.optionId === optionId;
    });
  };

  // Check customer matches first to see if the user has selected any brands from there
  if (customerBrandMatches.length > 0) {
    brandMatches.push(
      ...selectedBrandsOptionIds.flatMap(getMatchingBrand(customerBrandMatches))
    );
  }

  // If there are no customer matches, check the subcategory fallback matches
  if (customerBrandMatches.length === 0 && subcategoryBrandMatches.length > 0) {
    brandMatches.push(
      ...selectedBrandsOptionIds.flatMap(
        getMatchingBrand(subcategoryBrandMatches)
      )
    );
  }

  return brandMatches.map((brandMatch) => {
    return {
      brandFilterValue: brandMatch.brandFilterValue,
    };
  });
};

const getDeselectedBrands = (
  selectedBrands,
  brandsShown,
  supplierMatchSelectionEnabled,
  selectAndConnectBrand,
  predefinedSupplierId,
  isPredefinedSupplierWithBrandAvailable,
  dynamicSelectAndConnect
) => {
  // Nothing can be deselected in dynamic Select and Connect: the supplier match
  // is hidden, and the secondaries are only offered on the post-submission page.
  // Checked first so it holds on primary and secondary submissions alike, with
  // no explicit consent list present.
  if (dynamicSelectAndConnect?.enableDynamicSelectAndConnect) {
    return [];
  }

  if (isPredefinedSupplierWithBrandAvailable && !selectAndConnectBrand) {
    return [];
  }
  if (
    !supplierMatchSelectionEnabled ||
    typeof selectedBrands !== 'object' ||
    typeof brandsShown !== 'object' ||
    selectAndConnectBrand ||
    predefinedSupplierId
  ) {
    return undefined;
  }

  const selectedBrandIds = selectedBrands.map((brand) => brand.brandId);

  return brandsShown.filter(
    (brand) => !selectedBrandIds.includes(brand.brandId)
  );
};

const getShownLegalStatementHash = ({
  multipleSupplierConsentedLegalStatementHash,
  singleSupplierConsentedLegalStatementHash,
  consentedLegalStatementHash,
  selectAndConnectBrand,
  customerBrandMatches,
  supplierId,
  isPartial,
}) => {
  if (isPartial) return consentedLegalStatementHash;

  if (selectAndConnectBrand) {
    // Select and connect components always show a multi supplier statement
    return multipleSupplierConsentedLegalStatementHash;
  }

  if (
    shouldShowSingleSupplierStatement({
      supplierId,
      customerMatches: customerBrandMatches,
    })
  ) {
    return singleSupplierConsentedLegalStatementHash;
  }

  return consentedLegalStatementHash;
};

const getCustomerJourneyType = (
  selectAndConnectBrand,
  supplierMatchJourney,
  predefinedSupplierId,
  showSingleSupplierStatement,
  oneToOneSupplierConsent,
  dynamicSelectAndConnect
) => {
  if (selectAndConnectBrand) {
    return CustomerJourneyType.SELECT_AND_CONNECT;
  }
  // Checked before the dynamic branch: when a single-supplier statement was
  // shown the bundled hash is withheld, so the journey must classify as the
  // predefined-supplier model that the explicit consent list expresses.
  if (predefinedSupplierId || showSingleSupplierStatement) {
    return CustomerJourneyType.PREDEFINED_SUPPLIER;
  }
  // The dynamic Select and Connect primary is a full-list bundled opt-in with
  // no selections or deselections, which is what NO_MATCHING means downstream.
  // It must not be WITH_DESELECTIONS: the leads platform branches on that
  // value and would reprocess the consent hash through the deselections path
  // (GOLD-2441). The matches shown are recorded on shownToCustomer.
  if (dynamicSelectAndConnect?.enableDynamicSelectAndConnect) {
    return CustomerJourneyType.BUNDLED_CONSENT_NO_MATCHING;
  }
  if (supplierMatchJourney) {
    return oneToOneSupplierConsent
      ? CustomerJourneyType.BUNDLED_CONSENT_WITH_EXPLICIT_SELECTIONS
      : CustomerJourneyType.BUNDLED_CONSENT_WITH_DESELECTIONS;
  }
  return CustomerJourneyType.BUNDLED_CONSENT_NO_MATCHING;
};

/**
 * This concerns whether customers only had the option to be matched to a single supplier during their journey either through tcpa supplier matching or by having their supplier id predefined for the form.
 */
const isSingleSupplierPresented = (
  shouldIncludeSupplierMatch,
  predefinedSupplierId,
  brandsShownToCustomer
) => {
  if (!shouldIncludeSupplierMatch) return false;

  if (predefinedSupplierId) return true;

  return Boolean(
    typeof brandsShownToCustomer === 'object' &&
      brandsShownToCustomer.length === 1
  );
};

const areSupplierMatchesConditionallyActive = (
  components,
  shouldIncludeSupplierMatch
) => {
  if (!shouldIncludeSupplierMatch) return false;
  const firstSupplierMatchComponent = getSupplierMatchComponent({ components });
  return Boolean(
    firstSupplierMatchComponent &&
      isConditionallyActive(components, firstSupplierMatchComponent)
  );
};

const isSupplierMatchJourney = (
  components,
  shouldIncludeSupplierMatch,
  predefinedSupplierId,
  customerBrandMatches
) =>
  Boolean(
    areSupplierMatchesConditionallyActive(
      components,
      shouldIncludeSupplierMatch
    ) ||
      shouldShowSingleSupplierStatement({
        supplierId: predefinedSupplierId,
        customerMatches: customerBrandMatches,
      })
  );

const buildMatchReasonsShown = (brandMatchReasons, brandsShownToCustomer) => {
  if (!Array.isArray(brandMatchReasons) || brandMatchReasons.length === 0)
    return undefined;

  const shownBrandIds = Array.isArray(brandsShownToCustomer)
    ? new Set(brandsShownToCustomer.map((b) => b.brandId))
    : null;

  const result = brandMatchReasons.reduce(
    (accumulator, { brandId, reasons, generationType }) => {
      if (brandId && (!shownBrandIds || shownBrandIds.has(brandId))) {
        return [
          ...accumulator,
          { brandId, reasons: reasons || '', generationType },
        ];
      }
      return accumulator;
    },
    []
  );

  return result.length > 0 ? result : undefined;
};

const isSupplierMatchBlockedByNonUSARemovalsAutoqualifiedJourney = ({
  components,
  subcategoryId,
  phoneNumberCountryCode,
}) => {
  const selectedCountryIsUSA = Object.values(components).some(
    ({ component, input }) =>
      component.mappedQuestionId === SELECT_COUNTRY_MAPPED_QUESTION_ID &&
      input?.value?.trim() === 'USA'
  );

  return (
    subcategoryId === Subcategories.REMOVALS_AUTOQUALIFIED.id &&
    phoneNumberCountryCode !== 'US' &&
    !selectedCountryIsUSA
  );
};

const buildShownToCustomer = ({
  brandsShownToCustomer,
  selectAndConnectBrand,
  dynamicSelectAndConnect,
  dynamicMatches,
  supplierId,
  matchLevel,
}) => {
  if (dynamicSelectAndConnect?.enableDynamicSelectAndConnect) {
    // brandsShownToCustomer already carries the runtime dynamicMatches (the same
    // source as the widget and the GA4 selectAndConnectShown event). Record them,
    // or NO_MATCHES when the feature ran but resolved nothing to show.
    return {
      matchLevel: dynamicMatches?.[0]?.matchLevel ?? matchLevels.NO_MATCHES,
      matches: brandsShownToCustomer,
    };
  }

  if (
    Array.isArray(brandsShownToCustomer) &&
    brandsShownToCustomer.length > 0 &&
    !selectAndConnectBrand
  ) {
    return {
      matchLevel: supplierId ? 'PREDEFINED' : matchLevel,
      matches: brandsShownToCustomer,
    };
  }

  return undefined;
};

/**
 * The shownToCustomer record on its own, without any of the consent fields.
 * The BSA lead uses it on subcategory fallback matches, where it keeps the
 * bundled consent given at the phone step and only reports the matches shown.
 */
export const getShownToCustomer = ({
  components,
  customerBrandMatches = [],
  subcategoryBrandMatches = [],
  supplierMatchSelectionEnabled,
  supplierId,
  matchLevel,
  subcategoryId,
  phoneNumberCountryCode,
}) => {
  const isPredefinedSupplierWithBrandAvailable =
    shouldShowSingleSupplierStatement({
      supplierId,
      customerMatches: customerBrandMatches,
    });

  const shouldIncludeSupplierMatch =
    (supplierMatchSelectionEnabled || isPredefinedSupplierWithBrandAvailable) &&
    !isSupplierMatchBlockedByNonUSARemovalsAutoqualifiedJourney({
      components,
      subcategoryId,
      phoneNumberCountryCode,
    });

  if (!shouldIncludeSupplierMatch) return undefined;

  const brandsShownToCustomer = getBrandsShownToCustomer(
    Object.values(components).filter(isFormInputComponent),
    customerBrandMatches,
    subcategoryBrandMatches,
    supplierMatchSelectionEnabled,
    isPredefinedSupplierWithBrandAvailable
  );

  return buildShownToCustomer({
    brandsShownToCustomer,
    supplierId,
    matchLevel,
  });
};

export const getSubmissionArgs = ({
  ancestorSid,
  campaignId,
  countryCode,
  components,
  consentedSuppliersHash,
  consentedLegalStatementHash,
  formId,
  pageUrl,
  referrerUrl,
  phoneNumberCountryCode,
  sourceSite,
  testMode,
  widgetLabel,
  useCidFromURL,
  parentPageCookies,
  oneTrustLocale,
  customerBrandMatches = [],
  subcategoryBrandMatches = [],
  multipleSupplierConsentedLegalStatementHash,
  singleSupplierConsentedLegalStatementHash,
  consentCertification,
  supplierId,
  consentStatement,
  privacyPolicyUrl,
  subcategoryName,
  subcategoryId,
  matchLevel,
  selectAndConnectBrand,
  isPartial,
  isBsaOnly,
  supplierMatchSelectionEnabled,
  themeName,
  featureFlags,
  brandMatchReasons,
  enableResultsPage,
  dynamicSelectAndConnect,
  dynamicMatches,
  oneToOneSupplierConsent,
  bsaSubmissionId,
  bsaSubmissionToken,
  submissionType,
  strategyCollectionsBySubscriptionId,
}) => {
  const componentsUsedInSubmission = Object.values(components).filter(
    (component) => {
      if (isPartial && !isBsaOnly && hasPhoneAdmiralId(component)) {
        // We do not include phone number components in partial submissions since we do not have legal consent to collect them.
        return false;
      }

      return isFormInputComponent(component);
    }
  );

  // Reporting asks which match has a collection for the form's method. The
  // gate adds the collection's chameleonDag on top, so a matched placeholder
  // reports its method without a journey being offered.
  const matchedStrategyCollection = getMatchedStrategyCollection({
    customerMatches: customerBrandMatches,
    bySubscriptionId: strategyCollectionsBySubscriptionId,
    method: getOutreachAgentMethod(components),
  });

  const hasMatchedStrategyCollection = Boolean(matchedStrategyCollection);

  // Nested so the ids read as the journey's own, not as one of the several
  // other ways a lead relates to a supplier.
  const postSubmission = matchedStrategyCollection
    ? {
        subscriptionId: matchedStrategyCollection.subscriptionId,
        supplierId: matchedStrategyCollection.supplierId,
      }
    : undefined;

  const submissions = componentsUsedInSubmission.map((component) =>
    convertComponentToSubmissionObject(
      component,
      components,
      hasMatchedStrategyCollection
    )
  );

  const url = getUrl(pageUrl);

  const shownLegalStatementHash = getShownLegalStatementHash({
    multipleSupplierConsentedLegalStatementHash,
    singleSupplierConsentedLegalStatementHash,
    consentedLegalStatementHash,
    selectAndConnectBrand,
    customerBrandMatches,
    supplierId,
    isPartial,
  });

  const emailComponent = getEmailComponent({ components });
  const email = emailComponent?.input?.value;

  const supplierMatchJourneyBlockedByNonUSARemovalsAutoqualifiedJourney =
    isSupplierMatchBlockedByNonUSARemovalsAutoqualifiedJourney({
      components,
      subcategoryId,
      phoneNumberCountryCode,
    });

  const isPredefinedSupplierWithBrandAvailable =
    shouldShowSingleSupplierStatement({
      supplierId,
      customerMatches: customerBrandMatches,
    });

  const shouldIncludeSupplierMatch =
    (supplierMatchSelectionEnabled ||
      isPredefinedSupplierWithBrandAvailable ||
      selectAndConnectBrand ||
      dynamicSelectAndConnect?.enableDynamicSelectAndConnect) &&
    !supplierMatchJourneyBlockedByNonUSARemovalsAutoqualifiedJourney;

  let brandsConsentedByCustomer = getBrandsConsentedByCustomer(
    componentsUsedInSubmission,
    customerBrandMatches,
    subcategoryBrandMatches,
    supplierMatchSelectionEnabled,
    isPredefinedSupplierWithBrandAvailable,
    dynamicSelectAndConnect
  );

  const brandsShownToCustomer = getBrandsShownToCustomer(
    componentsUsedInSubmission,
    customerBrandMatches,
    subcategoryBrandMatches,
    supplierMatchSelectionEnabled,
    isPredefinedSupplierWithBrandAvailable,
    dynamicSelectAndConnect,
    dynamicMatches
  );

  const deselectedBrands = getDeselectedBrands(
    brandsConsentedByCustomer,
    brandsShownToCustomer,
    supplierMatchSelectionEnabled,
    selectAndConnectBrand,
    supplierId,
    isPredefinedSupplierWithBrandAvailable,
    dynamicSelectAndConnect
  );

  if (selectAndConnectBrand) {
    // Select and connect brands override the customer brands
    brandsConsentedByCustomer = [selectAndConnectBrand];
  }

  const shownToCustomer = buildShownToCustomer({
    brandsShownToCustomer,
    selectAndConnectBrand,
    dynamicSelectAndConnect,
    dynamicMatches,
    supplierId,
    matchLevel,
  });

  const supplierMatchJourney = shouldIncludeSupplierMatch
    ? isSupplierMatchJourney(
        components,
        shouldIncludeSupplierMatch,
        supplierId,
        customerBrandMatches
      )
    : false;

  return {
    lead: {
      countryCode,
      cookies: parentPageCookies,
      campaignId:
        getCampaignIdTrumpedByUrl(campaignId, url, useCidFromURL) ?? undefined,
      consentedSuppliersHash:
        selectAndConnectBrand || isPredefinedSupplierWithBrandAvailable
          ? undefined
          : consentedSuppliersHash,
      consentedLegalStatementHash: shownLegalStatementHash,
      formId: String(formId),
      formComponents: concat(submissions, {
        id: String(sourceSite.name).match(/[0-9]+/)[0],
        type: 'single',
        value: [sourceSite.value],
      }),
      supplierMatchJourney,
      singleSupplierPresented: isSingleSupplierPresented(
        shouldIncludeSupplierMatch,
        supplierId,
        brandsShownToCustomer
      ),
      originId: process.env.NEXT_PUBLIC_SUBMISSION_ORIGIN_ID,
      phoneNumberCountryCode: phoneNumberCountryCode ?? '',
      rsid: ancestorSid !== null ? ancestorSid : undefined, // we track a resubmissions original sid on the rsid value of a lead - can be undefined
      testMode: testMode ?? false,
      url: getUrlForSubmission(url, useCidFromURL),
      referrerUrl,
      uuid: generateUUID(),
      widgetLabel, // default of empty string
      oneTrustLocale,
      brandsConsentedByCustomer: shouldIncludeSupplierMatch
        ? brandsConsentedByCustomer
        : undefined,
      brandsDeselectedByCustomer: shouldIncludeSupplierMatch
        ? deselectedBrands
        : undefined,
      customerJourneyType: getCustomerJourneyType(
        selectAndConnectBrand,
        supplierMatchJourney,
        supplierId,
        isPredefinedSupplierWithBrandAvailable,
        oneToOneSupplierConsent,
        dynamicSelectAndConnect
      ),
      shownToCustomer: shouldIncludeSupplierMatch ? shownToCustomer : undefined,
      consentCertification,
      consentStatement,
      privacyPolicyUrl,
      email,
      subcategoryName,
      isPartial,
      isBsaOnly: isBsaOnly || undefined,
      bsaSubmissionId: bsaSubmissionId || undefined,
      bsaSubmissionToken: bsaSubmissionToken || undefined,
      theme: themeName,
      featureFlags,
      matchReasonsShown: enableResultsPage
        ? buildMatchReasonsShown(brandMatchReasons, brandsShownToCustomer)
        : undefined,
      submissionType,
      // The subscription and supplier the post-submission journey belongs to.
      // Only the widget knows which one matched, as the form holds every
      // collection in its subcategory.
      postSubmission,
    },
  };
};

export const getPartialSubmission = (components) => {
  const formInputComponents =
    Object.values(components).filter(isFormInputComponent);
  return formInputComponents.map((component) => {
    return {
      mappedQuestionId: component.component?.mappedQuestionId,
      value: getValueFromComponent(component),
      questionLabel: component.component?.text,
      useForAiMatching: isAnswerUsableForAiMatching(components, component),
    };
  });
};

export const shouldShowSubmissionBlockPage = (
  currentPage,
  submissionBlocks
) => {
  return submissionBlocks.some((block) => block.page < currentPage);
};
